import { useState } from 'react';
import { normalizeQuestionTitle } from '../utils/fileUtils';
import { Answer } from '../interfaces/answer.interface';
import { PdfSection, QuestionMapping } from '../interfaces/reports.interface';
import { ReportVersion } from '../interfaces/questionnaire.interface';

const formatAnswer = (value?: Answer | Answer[]): string | undefined => {
  if (!value) return 'N/A';
  if (Array.isArray(value)) {
    return value.map(v => v.text).filter(Boolean).join(', ');
  }
  return value.text;
};

export function formatPdfData(version: ReportVersion,
  type: string,
  {
    employeeCount,
    workFromHome,
    byod,
    thirdParty,
    contentTypes,
    physicalContent,
    timeframes,
    environments,
    dataCenter,
    replication,
    software,
    otherAssessments,
    zeroTrust,
    AIML,
    complianceTracking,
    KDM,
  }: {
    employeeCount?: Answer,
    workFromHome?: Answer;
    thirdParty?: Answer;
    contentTypes?: Answer[];
    physicalContent?: Answer;
    timeframes?: Answer[];
    dataCenter?: Answer;
    replication?: Answer;
    byod?: Answer;
    environments?: Answer[];
    software?: Answer;
    otherAssessments?: Answer[];
    zeroTrust?: Answer;
    AIML?: Answer[];
    complianceTracking?: Answer;
    KDM?: Answer;
  },
) {

  const layouts: Record<ReportVersion, Record<string, PdfSection[]>> = {
    // Questionnaire 5.3.1
    [ReportVersion.v5_3_1]: {
      'site': [
        {
          title: 'Organizational and Employees',
          rows: [
            { question: 'Do any employees work from home or from a remote location?', answer: formatAnswer(workFromHome) },
            { question: 'Are third-party Service Providers subcontracted for content handling, processing, or storage?', answer: formatAnswer(thirdParty) },
            { question: 'Is infrastructure or content handling, processing, or storage services provided on-premise or in the cloud?', answer: formatAnswer(environments) },
          ],
        },
        {
          title: 'Content Workflows',
          rows: [
            { question: 'What types of content does this site handle?', answer: formatAnswer(contentTypes) },
            { question: 'In what workflow timeframes do you handle content?', answer: formatAnswer(timeframes) },
            { question: 'Does this site handle any physical assets?', answer: formatAnswer(physicalContent) },
          ],
        },
        {
          title: 'Environments and Software',
          rows: [
            { question: 'Is software developed in-house for the purposes of storing, processing, or transferring content?', answer: formatAnswer(software) },
            { question: 'Does this site utilize Zero Trust Architecture (ZTA)?', answer: formatAnswer(zeroTrust) },
            { question: 'Does this site utilize any Artificial Intelligence (AI) or Machine Learning (ML) software as part of your production workflows?', answer: formatAnswer(AIML) },
          ],
        },
        {
          title: 'Other Security Assessments',
          rows: [
            { question: 'Has this site gone through a non-TPN security assessment with any other studio within the last 18 months?', answer: formatAnswer(otherAssessments) },
          ],
        },
      ],
      'application': [
        {
          title: 'Organizational and Employees',
          rows: [
            { question: 'Total number of full- and part-time employees that support the software development life cycle of the application.', answer: formatAnswer(employeeCount) },
            { question: 'Do any employees that support the software development life cycle and/or have access to content assets, work from home or from a remote location?', answer: formatAnswer(workFromHome) },
            { question: 'Are third-party Service Providers subcontracted to support the software development life cycle and/or other content asset related services?', answer: formatAnswer(thirdParty) },
          ],
        },
        {
          title: 'Content Workflows',
          rows: [
            { question: 'What types of content are handled by the application?', answer: formatAnswer(contentTypes) },
            { question: 'In what workflow timeframes does the application handle content?', answer: formatAnswer(timeframes) },
            { question: 'Do any application processes involve physical content assets?', answer: formatAnswer(physicalContent) },
          ],
        },
        {
          title: 'Environments and Software',
          rows: [
            { question: 'Is the application infrastructure hosted on-premise or in the cloud?', answer: formatAnswer(environments) },
            { question: 'Does this organization utilize any automated compliance tracking applications?', answer: formatAnswer(complianceTracking) },
            { question: 'Does this app utilize Zero Trust Architecture (ZTA)?', answer: formatAnswer(zeroTrust) },
            { question: 'Does this app utilize any Artificial Intelligence (AI) or Machine Learning (ML) functionalities?', answer: formatAnswer(AIML) },
          ],
        },
        {
          title: 'Other Security Assessments',
          rows: [
            { question: 'Has this app gone through a non-TPN security assessment with any other studio within the last 18 months?', answer: formatAnswer(otherAssessments) },
          ],
        },
      ],
    },

    // Questionnaire 5.3
    [ReportVersion.v5_3]: {
      'site|application': [
        {
          title: 'Organizational and Employees',
          rows: [
            { question: 'Do any employees work from home or from a remote location?', answer: formatAnswer(workFromHome) },
            { question: 'Are third-party Service Providers subcontracted for content handling, processing, or storage?', answer: formatAnswer(thirdParty) },
            { question: 'Is infrastructure or content handling, processing, or storage services provided on-site or in the cloud?', answer: formatAnswer(environments) },
            { question: 'Does the company allow BYOD?', answer: formatAnswer(byod) },
          ],
        },
        {
          title: 'Content Workflows',
          rows: [
            { question: 'What types of content does this site or app handle?', answer: formatAnswer(contentTypes) },
            { question: 'In what workflow timeframes do you handle content?', answer: formatAnswer(timeframes) },
            { question: 'Does this site or app handle any physical assets?', answer: formatAnswer(physicalContent) },
            { question: 'Does this site or app create KDMs for DCPs?', answer: formatAnswer(KDM) },
            { question: 'Does this site or app provide replication services (e.g., DVD, BD, etc.)?', answer: formatAnswer(replication) },
          ],
        },
        {
          title: 'Environments and Software',
          rows: [
            { question: 'Is software developed in-house for the purposes of storing, processing, or transferring content?', answer: formatAnswer(software) },
            { question: 'Does this site or app utilize an owned & operated data center and/or co-location?', answer: formatAnswer(dataCenter) },
            { question: 'Does this site or app utilize any automated compliance tracking applications?', answer: formatAnswer(complianceTracking) },
            { question: 'Does this site or app utilize Zero Trust Architecture (ZTA)?', answer: formatAnswer(zeroTrust) },
            { question: 'Does this site or app utilize any Artificial Intelligence (AI) or Machine Learning (ML) software as part of your production workflows?', answer: formatAnswer(AIML) },
          ],
        },
        {
          title: 'Other Security Assessments',
          rows: [
            { question: 'Has this site or app gone through a non-TPN security assessment with any other studio within the last 18 months?', answer: formatAnswer(otherAssessments) },
          ],
        },
      ],
    },
    
    // Questionnaire 5.2
    [ReportVersion.v5_2]: {
      'site|application': [
        {
          title: 'Organizational and Employees',
          rows: [
            { question: 'Do any employees work from home or from a remote location?', answer: formatAnswer(workFromHome) },
            { question: 'Are third-party Service Providers subcontracted?', answer: formatAnswer(thirdParty) },
            { question: 'Are services provided on-site and/or in the cloud?', answer: formatAnswer(environments) },
            { question: 'Does the company allow BYOD?', answer: formatAnswer(byod) },
          ],
        },
        {
          title: 'Content Workflows',
          rows: [
            { question: 'What types of content does this site or app handle?', answer: formatAnswer(contentTypes) },
            { question: 'In what workflow timeframes does this site or app handle content?', answer: formatAnswer(timeframes) },
            { question: 'Does this site or app handle any physical assets?', answer: formatAnswer(physicalContent) },
            { question: 'Does this site or app create KDMs for DCPs?', answer: formatAnswer(KDM) },
            { question: 'Does this site or app provide replication services (e.g., DVD, BD, etc.)?', answer: formatAnswer(replication) },
          ],
        },
        {
          title: 'Environments and Software',
          rows: [
            { question: 'Is software developed in-house?', answer: formatAnswer(software) },
            { question: 'Does this site or app utilize an owned & operated data center and/or co-location?', answer: formatAnswer(dataCenter) },
          ],
        },
      ],
    },


    [ReportVersion.v5_1]: {
      'site|application': [
        {
          title: 'Organizational and Employees',
          rows: [
            { question: 'Do any employees work from home or from a remote location?', answer: formatAnswer(workFromHome) },
            { question: 'Are services provided on-site and/or in the cloud?', answer: formatAnswer(environments) },
          ],
        },
        {
          title: 'Content Workflows',
          rows: [
            { question: 'What types of content does this site or app handle?', answer: formatAnswer(contentTypes) },
            { question: 'In what workflow timeframes does this site or app handle content?', answer: formatAnswer(timeframes) },
          ],
        },
      ],
    },
  };

  const versionLayouts = layouts[version] ?? {};
  // This allows us to do a substring key search for either "site" or "application" to get the relevant report structure
  const layout = versionLayouts[type] ?? Object.entries(versionLayouts).find(([key]) => key.split('|').includes(type))?.[1] ?? [];
  const body: PdfSection[][] = [];

  layout.forEach(section => {
    // Section title row
    body.push([
      { 
        text: section.title, 
        bold: true, 
        fillColor: '#eeeeee', 
        colSpan: 2,
        margin: [0, 10, 0, 4],
      }, 
      {},
    ]);

    // Each question row
    section.rows?.forEach(row => {
      body.push([
        { text: row.question, margin: [0, 3] },
        { text: row.answer ?? '', margin: [0, 3] },
      ]);
    });

    // Blank line between sections
    body.push([{ text: '', colSpan: 2 }, {}]);
  });

  return body;
}

export function usePdfReportQuestions() {
  const [employeeCount, setEmployeeCount] = useState<Answer | undefined>(undefined);
  const [workFromHome, setWorkFromHome] = useState<Answer | undefined>(undefined);
  const [byod, setBYOD] = useState<Answer | undefined>(undefined);
  const [thirdParty, setThirdParty] = useState<Answer | undefined>(undefined);
  const [contentTypes, setContentTypes] = useState<Answer[] | undefined>(undefined);
  const [physicalContent, setPhysicalContent] = useState<Answer | undefined>(undefined);
  const [timeframes, setTimeframes] = useState<Answer[] | undefined>(undefined);
  const [environments, setEnvironments] = useState<Answer[] | undefined>(undefined);
  const [dataCenter, setDataCenter] = useState<Answer | undefined>(undefined);
  const [replication, setReplication] = useState<Answer | undefined>(undefined);
  const [software, setSoftware] = useState<Answer | undefined>(undefined);
  const [otherAssessments, setOtherAssessments] = useState<Answer[] | undefined>(undefined);
  const [zeroTrust, setZeroTrust] = useState<Answer | undefined>(undefined);
  const [AIML, setAIML] = useState<Answer[] | undefined>(undefined);
  const [complianceTracking, setComplianceTracking] = useState<Answer | undefined>(undefined);
  const [KDM, setKDM] = useState<Answer | undefined>(undefined);


  const pdfReportQuestionMap: QuestionMapping[] = [
    { match: (title: string) => title.toLowerCase().includes('number') && title.toLowerCase().includes('employees'), setter: setEmployeeCount, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title).includes('Work From Home'), setter: setWorkFromHome, multiSelect: false },
    { match: (title: string) => ['Bring Your Own Device (BYOD)', 'Bring Your Own Device'].includes(normalizeQuestionTitle(title)), setter: setBYOD, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Subcontract to Third-Party Service Providers', setter: setThirdParty, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Content Types', setter: setContentTypes, multiSelect: true },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Workflow Timeframes', setter: setTimeframes, multiSelect: true },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Physical Content Assets', setter: setPhysicalContent, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Environments', setter: setEnvironments, multiSelect: true },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Data Center & Co-locations', setter: setDataCenter, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Replication Facilities', setter: setReplication, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Software Development', setter: setSoftware, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title) === 'Other Studio Security Assessments', setter: setOtherAssessments, multiSelect: true },
    { match: (title: string) => normalizeQuestionTitle(title).includes('Zero Trust Architecture'), setter: setZeroTrust, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title).includes('Artificial Intelligence (AI) and/or Machine Learning (ML)'), setter: setAIML, multiSelect: true },
    { match: (title: string) => normalizeQuestionTitle(title).includes('Compliance Tracking'), setter: setComplianceTracking, multiSelect: false },
    { match: (title: string) => normalizeQuestionTitle(title).includes('Key Delivery Messages'), setter: setComplianceTracking, multiSelect: false },
  ];

  return {
    employeeCount,
    workFromHome,
    byod,
    thirdParty,
    contentTypes,
    physicalContent,
    timeframes,
    environments,
    dataCenter,
    replication,
    software,
    otherAssessments,
    zeroTrust,
    AIML,
    complianceTracking,
    KDM,
    setEmployeeCount,
    setWorkFromHome,
    setBYOD,
    setThirdParty,
    setContentTypes,
    setPhysicalContent,
    setTimeframes,
    setEnvironments,
    setDataCenter,
    setReplication,
    setSoftware,
    setOtherAssessments,
    setZeroTrust,
    setAIML,
    setComplianceTracking,
    setKDM,
    pdfReportQuestionMap,
  };
}
